import DirectUploadWithRetry from './direct_upload_with_retry';
import showToast from './toast_notification';

// Uploads files added to a Trix editor (rich_text_area fields), in place of the listener @rails/actiontext registers.
//
// This is @rails/actiontext 7.2's AttachmentUpload (app/javascript/actiontext/attachment_upload.js) with two changes.
// The upload goes through DirectUploadWithRetry, so a tab holding a stale CSRF token (the user signed in again in
// another tab) refreshes the token and retries instead of failing; ActiveStorage::BaseController reports the refusal
// otherwise. And a failed upload removes the attachment and shows the wrapper's message, where the library throws
// into the console and leaves the attachment in the editor without a blob, so it is never saved with the text.
// The packs load this file instead of @rails/actiontext. Loading both would upload every attachment twice;
// spec/javascript/lib/direct_upload_imports.spec.js guards that. Compare with the upstream file on a Rails upgrade.

export default class TrixAttachmentUpload {
  constructor(attachment, element) {
    this.attachment = attachment;
    this.element = element;
    this.directUpload = new DirectUploadWithRetry(attachment.file, this.directUploadUrl, this);
  }

  start() {
    this.directUpload.create(this.directUploadDidComplete.bind(this));
  }

  directUploadWillStoreFileWithXHR(xhr) {
    xhr.upload.addEventListener('progress', (event) => {
      const progress = (event.loaded / event.total) * 100;
      this.attachment.setUploadProgress(progress);
    });
  }

  directUploadDidComplete(error, attributes) {
    if (error) {
      const visible = this.attachmentInEditor;
      this.attachment.remove();
      if (visible) showToast(error, { type: 'error', delay: 10000 });
      return;
    }

    this.attachment.setAttributes({
      sgid: attributes.attachable_sgid,
      url: this.createBlobUrl(attributes.signed_id, attributes.filename),
    });
  }

  // False once page script has removed the editor or the user has taken the attachment out, so a failure the user
  // can no longer see is not explained in a toast. remove() is a no-op for an attachment Trix no longer manages, so
  // it is still called either way.
  get attachmentInEditor() {
    if (!this.element.isConnected) return false;

    const editorDocument = this.element.editor?.getDocument();
    return !editorDocument || Boolean(editorDocument.getAttachmentById(this.attachment.id));
  }

  createBlobUrl(signedId, filename) {
    return this.blobUrlTemplate.replace(':signed_id', signedId).replace(':filename', encodeURIComponent(filename));
  }

  get directUploadUrl() {
    return this.element.dataset.directUploadUrl;
  }

  get blobUrlTemplate() {
    return this.element.dataset.blobUrlTemplate;
  }
}

window.addEventListener('trix-attachment-add', (event) => {
  const { attachment, target } = event;

  if (attachment.file) {
    const upload = new TrixAttachmentUpload(attachment, target);
    upload.start();
  }
});
